import { PaginationMeta } from 'components/ui/PaginationCompact';
import { CAPACITY_HORIZON_DAY_OPTIONS } from 'constants/project';
import {
  ProjectDetailTab,
  ProjectDetailsViewMode,
  PortfolioBadge,
  PortfolioBucket,
  PortfolioImportance,
  ForecastSegmentKind,
  ProjectLifecycleStatus,
  ProjectsListViewMode,
  SORT_MODE
} from 'enums';
import { ToDoTask } from './common.interface';
import { DateRangeFilter, PerspirationLevel } from './todos.interface';

export type { PaginationMeta };

export enum ProjectMemberRole {
  OWNER = 'owner',
  ADMIN = 'admin',
  MEMBER = 'member'
}

export enum ProjectMemberInvitationStatus {
  PENDING = 'pending',
  ACCEPTED = 'accepted',
  DECLINED = 'declined'
}

export interface ProjectStatus {
  id: string;
  label: string;
  color: string;
  order: number;
  should_complete_task?: boolean;
}

export interface ProjectMember {
  id: string;
  user_id?: string;
  email?: string;
  role: ProjectMemberRole;
  invitation_status: ProjectMemberInvitationStatus;
  invitation_sent_at?: string;
  invitation_responded_at?: string;
  created_at: string;
  updated_at: string;
}

export interface ProjectInvitationToken {
  email: string;
  admin_id: string;
  project_id: string;
  project_name: string;
  role: string;
  exp: number;
  iat?: number;
}

export interface PortfolioTopPriorityTask {
  id: string;
  title: string;
  top_score: number;
}

export interface Project {
  id: string;
  name: string;
  description?: string;
  owner_id: string;
  custom_statuses: ProjectStatus[];
  icon?: string;
  members?: ProjectMember[];
  task_count?: number;
  uncompleted_task_count?: number;
  synced_project_id?: string;
  due_date?: string | null;
  importance?: PortfolioImportance;
  /** Lifecycle state; absent on legacy payloads is treated as active. */
  status?: ProjectLifecycleStatus;
  /** Present on GET /projects; null = no open NEXT task. */
  top_priority_task?: PortfolioTopPriorityTask | null;
  created_at: string;
  updated_at: string;
}

export interface ProjectListResponse {
  data: Project[];
  meta: PaginationMeta;
}

export interface LegacyProjectListResponse {
  projects: Project[];
  total_count: number;
}

export interface CreateProjectPayload {
  name: string;
  description?: string;
  custom_statuses?: ProjectStatus[];
  due_date?: string | null;
  importance?: PortfolioImportance;
}

export interface UpdateProjectPayload {
  name?: string;
  description?: string;
  custom_statuses?: ProjectStatus[];
  due_date?: string | null;
  importance?: PortfolioImportance;
  status?: ProjectLifecycleStatus;
}

export interface InviteProjectMemberPayload {
  email: string;
  role?: ProjectMemberRole;
}

export interface UpdateProjectMemberPayload {
  role: ProjectMemberRole;
}

export interface ProjectDetailsFilters {
  taskName: string;
  perspirationLevel?: PerspirationLevel;
  dueDateRange?: DateRangeFilter | null;
  sortMode: SORT_MODE;
}

export interface ProjectSlice {
  projects: Project[];
  pendingInvitations: Project[];
  currentProject: Project | null;
  isProjectsLoading: boolean;
  isLoadingMoreProjects: boolean;
  isPendingInvitationsLoading: boolean;
  isProjectLoading: boolean;
  isCreatingProject: boolean;
  isUpdatingProject: boolean;
  isDeletingProject: boolean;
  isInvitingMember: boolean;
  isUpdatingMember: boolean;
  isRemovingMember: boolean;
  isAcceptingInvitation: boolean;
  isDecliningInvitation: boolean;
  isLeavingProject: boolean;
  showCreateProjectModal: boolean;
  showEditProjectModal: boolean;
  showInviteMemberModal: boolean;
  showProjectDetailsModal: boolean;
  pagination: PaginationMeta | null;
  projectDetails: {
    tasks: ToDoTask[];
    isTasksLoading: boolean;
    tasksError: string | null;
    mode: ProjectDetailsViewMode;
    activeTab: ProjectDetailTab;
    showFilters: boolean;
    filters: ProjectDetailsFilters;
    showMembersModal: boolean;
    showSettingsDrawer: boolean;
    showTaskDrawer: boolean;
    selectedTaskToEdit: ToDoTask | null;
    isCreatingTask: boolean;
    isUpdatingTask: boolean;
    isDeletingTask: boolean;
    isTaskStatusUpdating: boolean;
    taskStatusUpdatingIds: string[];
  };
  debouncedSearch: string;
  showCapacitySettings: boolean;
  showPendingInvitationsDrawer: boolean;
  listViewMode: ProjectsListViewMode;
  listStatusFilter: ProjectLifecycleStatus;
}

export interface FetchProjectsParams {
  page?: number;
  take?: number;
  order?: 'ASC' | 'DESC';
  completed_activity_id?: string;
  tag_id?: string;
  search?: string;
  status?: ProjectLifecycleStatus;
}

export interface MoveProjectDetailsKanbanTaskPayload {
  task: ToDoTask;
  targetColumnId: string;
  customStatuses?: ProjectStatus[];
}

export interface ProjectCustomStatusSection {
  id: string;
  label: string;
  color: string;
  tasks: ToDoTask[];
}

export interface PortfolioProjectRisk {
  id: string;
  name: string;
  due_date: string | null;
  importance: PortfolioImportance;
  required_hours: number;
  /** 0-100 */
  risk_score: number;
  bucket: PortfolioBucket;
  deficit_hours: number;
  days_until_due: number | null;
  latest_start: string | null;
  badges: PortfolioBadge[];
}

export interface PortfolioResponse {
  projects: PortfolioProjectRisk[];
  due_this_week: string[];
  start_now: string[];
  overcommitted: boolean;
  total_required_hours: number;
  total_available_hours: number;
}

/**
 * One day of this week's capacity split; sleep + events + habits + available === 24h.
 * When returned as `current_week_capacity`, ordered Monday…Sunday (length 7), matching
 * `weekly_capacity_hours` indices.
 */
export interface CurrentWeekDayCapacity {
  /** ISO date (YYYY-MM-DD) in the user's timezone. */
  date: string;
  awake_hours: number;
  sleep_hours: number;
  events_hours: number;
  habits_hours: number;
  available_hours: number;
}

export interface PortfolioSettings {
  /** length 7, index 0=Monday..6=Sunday */
  weekly_capacity_hours: number[];
  planning_horizon_days: number;
  /** When true, capacity is derived from the calendar (awake window − events − routines). */
  use_dynamic_capacity?: boolean;
  /** Read-only on responses: the capacity actually used (dynamic when enabled, else the grid). */
  effective_weekly_capacity_hours?: number[];
  /** Read-only: this week's Mon–Sun breakdown for the summary + day drilldown (dynamic mode). */
  current_week_capacity?: CurrentWeekDayCapacity[];
}

export interface ForecastProjectAllocation {
  project_id: string;
  project_name: string;
  hours: number;
}

/** One day of the time forecast: capacity split + the projects assigned to its available hours. */
export interface ForecastDay {
  /** ISO date (YYYY-MM-DD) in the user's timezone. */
  date: string;
  sleep_hours: number;
  events_hours: number;
  habits_hours: number;
  available_hours: number;
  allocated_hours: number;
  unallocated_hours: number;
  allocations: ForecastProjectAllocation[];
}

export interface ForecastProjectSummary {
  project_id: string;
  project_name: string;
  due_date: string | null;
  required_hours: number;
  scheduled_hours: number;
  remaining_hours: number;
  unschedulable_hours: number;
}

export interface PortfolioForecastResponse {
  days: ForecastDay[];
  projects: ForecastProjectSummary[];
  total_available_hours: number;
  total_required_hours: number;
  total_scheduled_hours: number;
  total_unschedulable_hours: number;
  /** True only in dynamic mode; when false the sleep/events/habits split is not populated. */
  has_capacity_breakdown: boolean;
  horizon_days: number;
}

/** One coloured slice of a forecast day's stacked time bar. */
export interface ForecastSegment {
  key: string;
  kind: ForecastSegmentKind;
  hours: number;
  percent: number;
  colorClass: string;
  /** Project name for project slices; fixed slices use i18n keys instead. */
  label?: string;
}

export interface ForecastSegmentLabelParts {
  readonly labelKey: string | null;
  readonly plainLabel: string;
}

export interface BuildForecastDaySegmentsArgs {
  readonly day: ForecastDay;
  readonly hasCapacityBreakdown: boolean;
  readonly projectColorById: Record<string, string>;
}

export type CapacityHorizonDayOption =
  (typeof CAPACITY_HORIZON_DAY_OPTIONS)[number];

export interface WeekdayGrossSample {
  readonly weekdayIndex: number;
  readonly grossHours: number;
}

/** '' lets the user clear an input while typing; treated as 0 on save. */
export type CapacityHoursInputValue = number | '';

export interface RecommendedTodayItem {
  id: string;
  name: string;
  dailyHours: number;
}

export interface RecommendedTodayPlan {
  visibleItems: RecommendedTodayItem[];
  totalDailyHours: number;
  hiddenCount: number;
}

export interface PaceDurationParts {
  hours: number;
  minutes: number;
}

export interface PortfolioActionGroups {
  askForExtension: PortfolioProjectRisk[];
  startNow: PortfolioProjectRisk[];
  backBurner: PortfolioProjectRisk[];
}

export interface PortfolioSlice {
  portfolio: PortfolioResponse | null;
  isLoading: boolean;
  topPriorityTasksByProjectId: Record<string, PortfolioTopPriorityTask | null>;
  settings: PortfolioSettings | null;
  isSettingsLoading: boolean;
  isSavingSettings: boolean;
  forecast: PortfolioForecastResponse | null;
  isForecastLoading: boolean;
}

export type PortfolioTopPriorityTaskByProjectId = Record<
  string,
  PortfolioTopPriorityTask | null
>;

export interface IsTrueZeroProjectsToolbarParams {
  readonly listViewMode: ProjectsListViewMode;
  readonly listStatusFilter: ProjectLifecycleStatus;
  readonly debouncedSearch: string;
  readonly isProjectsLoading: boolean;
  readonly projectCount: number;
}

export interface ProjectLifecycleActionVisibility {
  readonly showMarkComplete: boolean;
  readonly showArchive: boolean;
  readonly showRestore: boolean;
}
